import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Feature } from '@weavix/models/src/features/feature';
import { HttpService } from 'app/services/http.service';
import { MatDialog } from '@angular/material/dialog';
import { FeatureUsage } from '@weavix/models/src/features/feature-usage';
import { Site } from 'app/models/control-center/site.model';

@Component({
    selector: 'app-features',
    standalone: false,
    templateUrl: './features.component.html',
    styleUrl: './features.component.scss',
})
export class FeaturesComponent implements OnInit {
    @ViewChild('updateDialog') updateDialog: TemplateRef<unknown>;

    loading = true;
    childLoading = false;
    /** The features listed in the table */
    dataSource: Feature[] = [];
    /** Represents the table row that was clicked. */
    selectedFeature: Feature;
    /** Holds the value for the checkbox displayed in the feature modal */
    enabledCheckboxValue = false;
    /** Keeps track of the usages for each feature */
    featureUsage: FeatureUsage;
    /** Holds the enablement status of every site for a single feature at a time */
    siteEnablement: { [weavixAccountId: string]: {
        enabled: number;
        disabled: number;
    }; } = {};

    enabledFeatureCount: { [weavixAccountId: string]: number } = {};

    displayedColumns: any = [
        {
            key: 'removed',
            label: '',
            type: 'string',
            value: (row: Feature) => row?.removed ? '⚠️' : ' ',
        },
        {
            key: 'name',
            label: 'Feature',
            type: 'string',
            value: (row: Feature) => row?.name,
        },
        {
            key: 'description',
            label: 'Description',
            type: 'string',
            value: (row: Feature) => row?.description,
        },
        {
            key: 'lastUpdated',
            label: 'Last Updated',
            type: 'date',
            value: (row: Feature) => row?.lastUpdated,
        },
        {
            key: 'defaultValue',
            label: 'Default Value',
            type: 'string',
            value: (row: Feature) => row?.defaultValue ? 'Enabled' : 'Disabled',
        },
    ];

    constructor(private readonly httpService: HttpService, private readonly dialog: MatDialog) {
        this.ngOnInit();
    }

    async ngOnInit() {
        this.loading = true;
        try {
            this.dataSource = await this.loadFeatures();
        } catch (e) {
            console.error('Failed to load features', e);
        } finally {
            this.loading = false;
        }
    }

    async loadFeatures(): Promise<Feature[]> {
        try {
            const features: Feature[] = await this.httpService.get(`companies/weavix-account/features`);
            return features.filter(f => !f.hidden); // Exclude hidden features.
        } catch (e) {
            alert('Failed to load features');
            console.error('Failed to load features', e);
            return [];
        }
    }

    public async onRowClick(featureId: string) {
        this.childLoading = true;
        this.selectedFeature = this.dataSource.find((f) => f.id === featureId);
        this.enabledCheckboxValue = this.selectedFeature.defaultValue;
        this.dialog.open(this.updateDialog);
        this.featureUsage = await this.httpService.get(`companies/weavix-account/features/${featureId}`);
        try {
            // have to query the feature flags for each weavix account to get the enabled/disabled counts
            await Promise.all(Object.keys(this.featureUsage).map(async weavixAccountId => {
                // if using the feature's default value then skip making http calls.
                // this cuts down the loading time significantly.
                if (this.featureUsage[weavixAccountId].accountSetting !== undefined) {
                    return this.siteEnablement[weavixAccountId] = {
                        enabled: 0,
                        disabled: 0,
                    };
                }

                // get the feature flags for the weavix account
                const featureFlags = await this.httpService.get(`companies/weavix-account/${weavixAccountId}/feature-flags`);
                // get the sites for the weavix account to cross check them against the feature flags
                const weavixAccountSites: Site[] = await this.httpService.get(`companies/weavix-account/${weavixAccountId}/sites`);

                // start count
                let featureEnabledCount = 0;
                weavixAccountSites.forEach(site => {
                    // check if the feature flag is enabled for the site
                    const ffEnabled = featureFlags.find(ff => ff.id.facilityId === site.weavixSiteId
                        && ff.id.featureId === this.selectedFeature.id)?.enabled;
                    // if it is, increment the count
                    if (ffEnabled) {
                        featureEnabledCount++;
                    }
                    // if it is not explicitly disabled, then we check if the feature's default value is enabled
                    // and increment the count if it is.
                    else if (ffEnabled !== false && this.selectedFeature.defaultValue) {
                        featureEnabledCount++;
                    }
                });

                // return dictionary with enabled and disabled counts
                return this.siteEnablement[weavixAccountId] = {
                    enabled: featureEnabledCount,
                    disabled: weavixAccountSites.length - featureEnabledCount,
                };
            }));
        } catch (e) {
            console.error('Failed to load features for all sites', e);
        } finally {
            this.childLoading = false;
        }
    }

    public async save() {
        this.loading = true;
        try {
            await this.httpService.put(`companies/weavix-account/features/${this.selectedFeature.id}`, {
                enabledByDefault: this.enabledCheckboxValue,
            });
            this.dialog.closeAll();
            await this.ngOnInit();
        } catch (e) {
            alert('Failed to update feature');
            console.error('Failed to update feature', e);
        } finally {
            this.loading = false;
        }
    }
}
